import { type ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { DataTable } from "@/components/data-table";
import { Checkbox } from "@/components/ui/checkbox";
import { PageContainer, PageHeader } from "@/components/layout";
import { useNotificationsProvider } from "../../context/NotificationsContext";
import { permissionTree, translate } from "../../entities/permissions";
import {
  useGrantPermissionMutation,
  usePermissionAdminListQuery,
  useRevokePermissionMutation,
} from "../../services/api";

interface PermissionRow {
  key: string;
  translation: string;
  page: string;
  func: string;
  roles: Record<string, boolean> | undefined;
}

interface PermissionsState {
  permissions?: Record<string, Record<string, boolean>>;
  roles?: Record<string, unknown>;
}

/*
 * Flatten the static permissionTree into one row per leaf, preserving
 * the root-of-page ordering so the UI groups by feature page. Roots
 * are tagged via `func === 'root'` so the DataTable highlight prop can
 * pick them out (replaces the styled TableRow / customRowRender from
 * the old BFTable consumer).
 */
function buildRows(permissions: PermissionsState | null | undefined, t: TFunction): PermissionRow[] {
  const grants = permissions?.permissions ?? {};
  const rows: PermissionRow[] = [];

  Object.keys(permissionTree).forEach((rootKey) => {
    rows.push({
      key: rootKey,
      translation: translate(rootKey, t),
      page: rootKey.split(":")[0],
      func: rootKey.split(":")[1],
      roles: grants[rootKey],
    });
    (permissionTree as Record<string, string[]>)[rootKey].forEach((subKey) => {
      rows.push({
        key: subKey,
        translation: translate(subKey, t),
        page: subKey.split(":")[0],
        func: subKey.split(":")[1],
        roles: grants[subKey],
      });
    });
  });

  return rows;
}

export default function Permissions() {
  const { t } = useTranslation();
  const { showNotification } = useNotificationsProvider();

  const { data: permissions } = usePermissionAdminListQuery() as {
    data?: PermissionsState | null;
  };
  const [grantPermission] = useGrantPermissionMutation();
  const [revokePermission] = useRevokePermissionMutation();

  const rows = useMemo(() => buildRows(permissions, t), [permissions, t]);
  const roles = permissions?.roles ?? null;

  async function toggleRole(
    row: PermissionRow,
    role: string,
    currentlyChecked: boolean,
  ) {
    const payload = { role, permission: row.key };
    try {
      if (currentlyChecked) {
        await revokePermission(payload).unwrap();
      } else {
        await grantPermission(payload).unwrap();
      }
    } catch {
      showNotification("error", t("common.api-error"));
    }
  }

  const columns = useMemo<ColumnDef<PermissionRow>[]>(() => {
    const base: ColumnDef<PermissionRow>[] = [
      {
        accessorKey: "translation",
        header: t("permissions.items.features"),
        cell: ({ row }) => (
          <span className={row.original.func === "root" ? "font-bold" : ""}>
            {row.original.translation}
          </span>
        ),
      },
    ];
    if (roles) {
      Object.keys(roles).forEach((roleKey) => {
        base.push({
          id: roleKey,
          header: t(`permissions.items.${roleKey}`),
          enableSorting: false,
          cell: ({ row }) => {
            const checked = row.original.roles?.[roleKey] ?? false;
            return (
              <Checkbox
                checked={checked}
                onCheckedChange={() => toggleRole(row.original, roleKey, checked)}
                aria-label={`${row.original.translation} — ${roleKey}`}
              />
            );
          },
        });
      });
    }
    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles, t]);

  return (
    <PageContainer title={t("permissions.tab-title")}>
      <PageHeader
        title={t("permissions.page-title")}
        description={t("permissions.info-message")}
      />
      <div className="mt-6">
        <DataTable
          columns={columns}
          data={rows}
          pagination={false}
          emptyMessage={t("common.no-data", "No data")}
          getRowClassName={(row) =>
            row.original.func === "root" ? "bg-muted/40" : undefined
          }
        />
      </div>
    </PageContainer>
  );
}
